import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Clock, MapPin } from "lucide-react";
import { Reveal, ChapterHead } from "./Reveal";

const SUBURBS = [
  { name: "Sydney CBD", slug: "sydney-cbd", eta: "3–5 min" },
  { name: "Bondi", slug: "bondi", eta: "4–7 min" },
  { name: "Manly", slug: "manly", eta: "5–8 min" },
  { name: "Parramatta", slug: "parramatta", eta: "4–7 min" },
  { name: "Mascot Airport", slug: "mascot-airport", eta: "3–6 min" },
  { name: "North Shore", slug: "north-shore", eta: "5–9 min" },
];

const EXTRA_SUBURB_PAGES = [
  { name: "Newtown", slug: "newtown" },
  { name: "Leichhardt", slug: "leichhardt" },
  { name: "Drummoyne", slug: "drummoyne" },
  { name: "Five Dock", slug: "five-dock" },
  { name: "Balmain", slug: "balmain" },
  { name: "Haberfield", slug: "haberfield" },
  { name: "Rozelle", slug: "rozelle" },
];

const ALL_SUBURB_PAGES = [...SUBURBS, ...EXTRA_SUBURB_PAGES];

const STREET_IMG =
  "https://images.unsplash.com/photo-1601167936291-4e620b9894c2?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

export const Coverage = () => {
  const [active, setActive] = useState(0);
  const sub = SUBURBS[active];

  return (
    <section id="coverage" className="relative py-24 sm:py-32">
      <div className="mx-auto max-w-7xl px-5 sm:px-8 grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
        <Reveal>
          <div className="relative rounded-3xl overflow-hidden border border-gold-400/15 aspect-[4/5] max-h-[560px] w-full">
            <img
              src={STREET_IMG}
              alt="Reliable Sydney taxi on a city street at night"
              loading="lazy"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/20 to-transparent" />
            <div className="absolute bottom-6 left-6 right-6 glass border border-gold-400/25 rounded-2xl px-5 py-4">
              <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-gold-300 mb-1">
                Live dispatch simulator
              </p>
              <p className="text-sm text-white/90">
                The closest cab to <span className="text-gold-300 font-semibold">{sub.name}</span> is
                always one tap away.
              </p>
            </div>
          </div>
        </Reveal>

        <div>
          <ChapterHead
            num="CHAPTER 03"
            kicker="Coverage"
            title={'Searching "taxi near me"? We are already close'}
          />
          <Reveal delay={0.1}>
            <p className="text-[#99A1B3] mb-8 max-w-lg">
              From the CBD to the coast, tap a zone to see a typical pickup window. Wherever you
              are in Sydney, a local taxi is minutes away — 24 hours a day.
            </p>
          </Reveal>

          <div data-testid="coverage-suburb-select" className="flex flex-wrap gap-2.5 mb-8">
            {SUBURBS.map((s, i) => (
              <button
                key={s.name}
                data-testid={`coverage-suburb-${s.name.toLowerCase().replace(/\s+/g, "-")}`}
                onClick={() => setActive(i)}
                className={`rounded-full px-5 py-2.5 text-sm font-semibold border transition-all duration-200 ${
                  i === active
                    ? "bg-gold-400 text-ink border-gold-400"
                    : "border-gold-400/25 text-[#99A1B3] hover:border-gold-400/50 hover:text-white"
                }`}
              >
                {s.name}
              </button>
            ))}
          </div>

          <AnimatePresence mode="wait">
            <motion.div
              key={sub.name}
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.3 }}
              data-testid="coverage-eta-panel"
              className="bg-ink-800 border border-gold-400/20 rounded-2xl p-6 flex items-center gap-6"
            >
              <div className="shrink-0 h-16 w-16 rounded-2xl bg-gold-400/10 border border-gold-400/30 flex flex-col items-center justify-center">
                <Clock size={18} className="text-gold-400" />
                <span className="font-mono text-[9px] text-gold-300 mt-1">ETA</span>
              </div>
              <div>
                <p className="font-display text-2xl font-extrabold text-white" data-testid="coverage-eta-value">
                  {sub.eta}
                </p>
                <p className="text-sm text-[#99A1B3] flex items-center gap-1.5 mt-0.5">
                  <MapPin size={13} className="text-gold-400" /> typical pickup in {sub.name}
                </p>
              </div>
            </motion.div>
          </AnimatePresence>

          <Reveal delay={0.15}>
            <p className="mt-6 text-xs text-[#626B7F]">
              Pickup windows are dispatch estimates and vary with traffic and time of day. Also
              serving: Inner West, Eastern Suburbs, Hills District, Sutherland Shire.
            </p>
            <p data-testid="suburb-pages-links" className="mt-3 text-xs text-[#626B7F]">
              Dedicated pages:{" "}
              {ALL_SUBURB_PAGES.map((s, i) => (
                <span key={s.name}>
                  <a
                    href={`/taxi-near-me-${s.slug}.html`}
                    data-testid={`suburb-page-link-${s.slug}`}
                    className="text-gold-300 underline underline-offset-2 hover:text-gold-200 transition-colors"
                  >
                    Taxi near me in {s.name}
                  </a>
                  {i < ALL_SUBURB_PAGES.length - 1 ? " · " : ""}
                </span>
              ))}
            </p>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
